import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ShieldAlert,
  RefreshCw,
  PlayCircle,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Search,
} from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { StatusBadge } from "../components/ui/StatusBadge";
import { KPICard } from "../components/ui/KPICard";
import { Input } from "../components/ui/Input";
import api from "../lib/api";
import { useAppStore } from "../stores/appStore";
import { formatDate } from "../lib/utils";

interface RiskAssessment {
  id: string;
  asset_id: string;
  asset_name?: string;
  risk_score?: number;
  likelihood?: number;
  consequence?: number;
  risk_category?: string;
  status: string;
  notes?: string;
  assessed_at: string;
  assessed_by?: string;
  created_at: string;
}

export default function Risk() {
  const { selectedOrgId, selectedSiteId } = useAppStore();
  const [assessments, setAssessments] = useState<RiskAssessment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isBatching, setIsBatching] = useState(false);
  const [search, setSearch] = useState("");

  const fetchAssessments = useCallback(async () => {
    if (!selectedOrgId) return;
    setIsLoading(true);
    try {
      const params: Record<string, string | number> = {
        page,
        page_size: 20,
      };
      if (selectedSiteId) params.virtual_site_id = selectedSiteId;

      const res = await api.get(
        `/risk/assessments`,
        { params }
      );
      setAssessments(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      setAssessments([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrgId, selectedSiteId, page]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  async function handleBatchAssess() {
    if (!selectedOrgId || !selectedSiteId) return;
    setIsBatching(true);
    try {
      await api.post(
        `/risk/assess`,
        null,
        {
          params: { virtual_site_id: selectedSiteId },
        }
      );
      fetchAssessments();
    } catch {
      /* batch may fail silently */
    } finally {
      setIsBatching(false);
    }
  }

  const highRisk = assessments.filter(
    (a) => a.risk_score !== undefined && a.risk_score >= 15
  ).length;
  const medRisk = assessments.filter(
    (a) =>
      a.risk_score !== undefined && a.risk_score >= 8 && a.risk_score < 15
  ).length;
  const lowRisk = assessments.filter(
    (a) => a.risk_score !== undefined && a.risk_score < 8
  ).length;
  const scores = assessments
    .map((a) => a.risk_score)
    .filter((s): s is number => s !== undefined);
  const avgScore =
    scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : "0";

  function riskColor(score: number | undefined) {
    if (score === undefined) return "text-surface-400";
    if (score >= 15) return "text-red-400";
    if (score >= 8) return "text-amber-400";
    return "text-emerald-400";
  }

  function riskBg(score: number | undefined) {
    if (score === undefined) return "bg-surface-700/50";
    if (score >= 15) return "bg-red-500/15";
    if (score >= 8) return "bg-amber-500/15";
    return "bg-emerald-500/15";
  }

  const columns: ColumnDef<RiskAssessment, unknown>[] = [
    {
      accessorKey: "risk_score",
      header: "Score",
      cell: ({ row }) => (
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg text-lg font-bold ${riskBg(row.original.risk_score)} ${riskColor(row.original.risk_score)}`}
        >
          {row.original.risk_score ?? "-"}
        </div>
      ),
    },
    {
      accessorKey: "asset_name",
      header: "Asset",
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-surface-100">
            {row.original.asset_name || row.original.asset_id}
          </p>
          {row.original.risk_category && (
            <p className="text-xs text-surface-500">
              {row.original.risk_category}
            </p>
          )}
        </div>
      ),
    },
    {
      accessorKey: "likelihood",
      header: "L x C",
      cell: ({ row }) => (
        <span className="text-sm text-surface-300">
          {row.original.likelihood ?? "-"} x {row.original.consequence ?? "-"}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: "notes",
      header: "Notes",
      cell: ({ row }) => (
        <span className="text-surface-400 text-sm max-w-xs truncate block">
          {row.original.notes || "-"}
        </span>
      ),
    },
    {
      accessorKey: "assessed_at",
      header: "Assessed",
      cell: ({ row }) => (
        <span className="text-surface-400 text-sm">
          {formatDate(row.original.assessed_at || row.original.created_at)}
        </span>
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-surface-100">
              Risk Assessments
            </h1>
            <span
              className="rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300"
              title="This module is under active development"
            >
              Work in progress
            </span>
          </div>
          <p className="text-sm text-surface-400 mt-1">
            Evaluate and monitor risk across your assets
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-64">
            <Input
              placeholder="Search assessments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <Button
            variant="secondary"
            size="icon"
            onClick={fetchAssessments}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            onClick={handleBatchAssess}
            isLoading={isBatching}
            disabled={!selectedSiteId}
          >
            <PlayCircle className="h-4 w-4" />
            Batch Assess
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard
          title="Average Score"
          value={avgScore}
          icon={<TrendingUp className="h-5 w-5" />}
          color="brand"
        />
        <KPICard
          title="High Risk"
          value={highRisk}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="red"
        />
        <KPICard
          title="Medium Risk"
          value={medRisk}
          icon={<ShieldAlert className="h-5 w-5" />}
          color="amber"
        />
        <KPICard
          title="Low Risk"
          value={lowRisk}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="emerald"
        />
      </div>

      <DataTable
        columns={columns}
        data={assessments}
        isLoading={isLoading}
        enableSearch={false}
        searchValue={search}
        onSearchChange={setSearch}
        emptyMessage="No risk assessments found."
        page={page}
        totalPages={totalPages}
        total={total}
        onPageChange={setPage}
      />
    </motion.div>
  );
}
